import { hits, configure } from "instantsearch.js/es/widgets";
import { getInstance, registerWidget } from "./algolia-create-instance";
import { renderHitTemplate as fallbackTemplate } from "../../hits-templates/default-template.js";
import { renderManagedCardTemplate } from "./managed-card-template.js";
import { preFilterHits } from './algolia-pre-filter.js';
import { applyFilters, addFilter } from '@wordpress/hooks';
import { __, _n, sprintf } from './i18n.js';

/**
 * Derives the current user's enrollment status string for a hit.
 *
 * userCourseStatuses has the shape: { not_started: [id,...], in_progress: [id,...], completed: [id,...] }
 * objectID format: "sfwd-courses#30"
 *
 * Returns one of: 'completed' | 'in_progress' | 'not_started' | 'not_enrolled' | ''
 */
function resolveCourseEnrollmentStatus( hit, userCourseStatuses ) {
  if ( ! userCourseStatuses ) return '';
  const parts    = ( hit.objectID || '' ).split( '#' );
  const courseId = parts.length === 2 ? parseInt( parts[ 1 ], 10 ) : 0;
  if ( ! courseId ) return '';
  // PHP may encode arrays with non-sequential keys as plain JS objects —
  // normalise to a real array before calling .includes().
  const toArr = v => Array.isArray( v ) ? v : Object.values( v || {} );
  if ( toArr( userCourseStatuses.completed   ).includes( courseId ) ) return 'completed';
  if ( toArr( userCourseStatuses.in_progress ).includes( courseId ) ) return 'in_progress';
  if ( toArr( userCourseStatuses.not_started ).includes( courseId ) ) return 'not_started';
  return 'not_enrolled';
}

function initHitsWidget() {
  const tgblAlgoliaDataJSON = window.atob(tgblAlgoliaDataEncoded);
  const tgblAlgoliaData = jQuery.parseJSON(tgblAlgoliaDataJSON);
  // TODO: Translations
  //const LOCALIZED = tgblAlgoliaData.localized;

  const elements = document.querySelectorAll("[data-filter-type='hits']");
  const elementsArray = Array.from(elements);

  const elementsGroupedByIndex = elementsArray.reduce((acc, element) => {
    const dataIndexValue = element.getAttribute("data-index");
    if (!acc[dataIndexValue]) {
      acc[dataIndexValue] = [];
    }
    acc[dataIndexValue].push(element);
    return acc;
  }, {});

  for (const dataIndexValue in elementsGroupedByIndex) {
    if (elementsGroupedByIndex.hasOwnProperty(dataIndexValue)) {
      const elementsWithSameIndex = elementsGroupedByIndex[dataIndexValue];
      let search = getInstance(dataIndexValue);
      
      elementsWithSameIndex.forEach(async element => {
        
        const widgetID                  = element.getAttribute('data-id');
        const widget                    = tgblAlgoliaData[widgetID];
        const templatePath              = widget['template_path'];
        const cardTemplate              = widget['card_template'];
        const cardTemplateHtml          = widget['card_template_html'];
        const preFilterItemsObject      = widget['pre_filter_items'];
        const facetMappingsByAttribute  = widget['facet_mappings_by_attribute'] || {};
        const userCourseStatuses        = widget['user_course_statuses'] || null;

        if ( preFilterItemsObject ) {
          let preFilterItems = Object.values( preFilterItemsObject ).flat();

          if ( preFilterItems.length !== 0 ) {
            preFilterHits( preFilterItems, dataIndexValue );
          }
        }

        let renderHitTemplate = fallbackTemplate;
        let isInlineLayout    = false;

        if ( cardTemplateHtml ) {
          const tmp   = document.createElement( 'div' );
          tmp.innerHTML = cardTemplateHtml;
          const root  = tmp.querySelector( '[data-template]' );
          isInlineLayout = root?.dataset?.layout === 'inline';
        }

        if ( cardTemplate && cardTemplateHtml ) {
          // Managed template path: slot-based pre-built template.
          renderHitTemplate = ( hit, { html, component } ) => {
            const augmentedHit = userCourseStatuses
              ? { ...hit, users_course_enrollment_status: resolveCourseEnrollmentStatus( hit, userCourseStatuses ) }
              : hit;
            return renderManagedCardTemplate( augmentedHit, cardTemplate, cardTemplateHtml, html, facetMappingsByAttribute );
          };

        } else if ( templatePath ) {
          // Custom template path (old {hit.field} style).
          // TODO: implement full custom-template support once the managed path is stable.
          try {
            const module = await import(templatePath);
            renderHitTemplate = module.default || module.renderHitTemplate;
          } catch (error) {
            console.error(
              `Error loading template from ${templatePath}:`,
              error,
            );
          }
        }

        let AlgoliaHits = ($, search, element) => {
          search.addWidgets([
            hits({
              container: element,
              templates: {
                item: (hit, { html, component }) => {
                  const template = renderHitTemplate(hit, { html, component, __, _n, sprintf }, widgetID);
                  const event = new CustomEvent("algoliaRenderTemplate", {});
                  window.dispatchEvent(event);
                  return applyFilters(
                    'tangible-algolia-hit-template',
                    template,
                    { hit, html, component, widget }
                  )
                },
              },
              cssClasses: {
                list: isInlineLayout
                  ? [ 'tgblAlgolia-grid', 'tgblAlgolia-grid--inline' ]
                  : [ 'tgblAlgolia-grid', 'tgblAlgolia-grid--density-16' ],
                item: [
                  "tgblAlgolia-card tgblAlgolia-card--is-style-elevated tgblAlgolia-card--has-interaction",
                ],
              },
            }),
          ]);
          registerWidget(element);
        };
        AlgoliaHits($, search, element);
      });
    }
  }
}
  
export { initHitsWidget }